// Insurance claims — shared lifecycle + formatting helpers used by
// ClaimsCard, ClaimDrawer and ClaimStatusTimeline.

import { formatCurrency } from "@/design-system/lib/format";
import type {
  InsuranceClaimDto,
  InsuranceClaimStatus,
} from "@/api/portfolioAdmin/insuranceClaimsApi";

/** Legal transitions: Filed→Acknowledged→Adjusting→Settled|Denied. */
export function nextStagesOf(
  status: InsuranceClaimStatus,
): InsuranceClaimStatus[] {
  switch (status) {
    case "Filed":
      return ["Acknowledged"];
    case "Acknowledged":
      return ["Adjusting"];
    case "Adjusting":
      return ["Settled", "Denied"];
    case "Settled":
    case "Denied":
      return [];
  }
}

export function isOpenClaim(claim: Pick<InsuranceClaimDto, "status">): boolean {
  return claim.status !== "Settled" && claim.status !== "Denied";
}

/** Timestamp the claim reached a given stage, if it has. */
export function stageDateOf(
  claim: InsuranceClaimDto,
  stage: InsuranceClaimStatus,
): string | null {
  switch (stage) {
    case "Filed":
      return claim.filedAt;
    case "Acknowledged":
      return claim.acknowledgedAt;
    case "Adjusting":
      return claim.adjustingAt;
    case "Settled":
      return claim.settledAt;
    case "Denied":
      return claim.deniedAt;
  }
}

/** `$#,##0.00`; negatives in parens; null → em dash. */
export function formatClaimAmount(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  const formatted = formatCurrency(Math.abs(n), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return n < 0 ? `(${formatted})` : formatted;
}

export const CLAIM_STATUS_BADGE: Record<
  InsuranceClaimStatus,
  "info" | "review" | "finalized" | "error" | "draft"
> = {
  Filed: "info",
  Acknowledged: "draft",
  Adjusting: "review",
  Settled: "finalized",
  Denied: "error",
};
